import React from "react";
import { NavLink, useMatch } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@fluentui/react-components";
import { ArchiveRegular } from "@fluentui/react-icons/svg/archive";
import { ChartMultipleRegular } from "@fluentui/react-icons/svg/chart-multiple";
import { ClipboardTaskListLtrRegular } from "@fluentui/react-icons/svg/clipboard-task-list-ltr";
import { PlugConnectedRegular } from "@fluentui/react-icons/svg/plug-connected";
import { SettingsRegular } from "@fluentui/react-icons/svg/settings";
import { useGuardedLinkClick } from "@contexts/NavigationGuardContext";
import { useIsDrawerViewport } from "@/hooks/useIsPhoneViewport";

const NON_BOARD_ROUTES = new Set(["loading", "dashboard", "regions"]);

interface NavEntry {
  key: string;
  to: string;
  label: string;
  icon: React.ReactElement;
  tourId?: string;
  /** Never shows its label, however much room the header has. */
  iconOnly?: boolean;
  /** Marked active on a route other than its own. */
  alsoActive?: boolean;
}

/**
 * Board navigation, in the header.
 *
 * It used to live in the left rail, which meant it vanished on Connections,
 * Numbers and Archive — those render in a layout with no rail. In the header it
 * survives every route, and the rail is freed to become the board's filter
 * pane.
 *
 * Below the drawer breakpoint this renders nothing: the header is already full
 * at those widths, so navigation stays in the drawer, where the rail still
 * shows it.
 */
const AppHeaderNav: React.FC = () => {
  const { t } = useTranslation();
  const isDrawer = useIsDrawerViewport();
  // A page with unsaved edits gets to prompt before the nav takes the user off it.
  const guardedLinkClick = useGuardedLinkClick();

  const boardMatch = useMatch("/:boardId/*");
  const rawBoardId = boardMatch?.params.boardId;
  const boardId =
    rawBoardId && !NON_BOARD_ROUTES.has(rawBoardId) ? rawBoardId : undefined;
  const isScheduleRoute = useMatch("/:boardId/schedule") !== null;

  if (isDrawer || !boardId) {
    return null;
  }

  const entries: NavEntry[] = [
    {
      key: "tasks",
      to: `/${boardId}/tasks`,
      label: t("layout.tabs.tasks"),
      icon: <ClipboardTaskListLtrRegular />,
      // The schedule is the board's other view of the same tasks, reached from
      // the toolbar toggle, so it has no entry here and Tasks stands for it.
      alsoActive: isScheduleRoute,
    },
    // Connections sits second: it is where a board gets its tasks from, so it
    // is reached far more often than Numbers or Archive.
    {
      key: "connections",
      to: `/${boardId}/connections`,
      label: t("layout.tabs.connections"),
      icon: <PlugConnectedRegular />,
      tourId: "sidebar-connections",
    },
    {
      key: "numbers",
      to: `/${boardId}/numbers`,
      label: t("layout.tabs.numbers"),
      icon: <ChartMultipleRegular />,
    },
    {
      key: "hidden",
      to: `/${boardId}/hidden`,
      label: t("layout.tabs.hiddenTasks"),
      icon: <ArchiveRegular />,
    },
    // Icon-only at every width: it is reached rarely, and the label would cost
    // room the four destinations above make better use of.
    {
      key: "settings",
      to: `/${boardId}/settings`,
      label: t("layout.tabs.settings"),
      icon: <SettingsRegular />,
      iconOnly: true,
    },
  ];

  return (
    <nav
      className="app-header__nav"
      data-tour="header-nav"
      aria-label={t("layout.sidebar.mainNavigationAria")}
    >
      {entries.map(({ key, to, label, icon, tourId, iconOnly, alsoActive }) => (
        // The label is hidden by CSS on a narrower header, so every entry
        // carries a tooltip rather than only the ones that lose it.
        <Tooltip key={key} content={label} relationship="label">
          <NavLink
            to={to}
            onClick={guardedLinkClick(to)}
            {...(tourId ? { "data-tour": tourId } : {})}
            className={({ isActive }) =>
              `app-header__nav-link${isActive || alsoActive ? " app-header__nav-link--active" : ""}${iconOnly ? " app-header__nav-link--icon-only" : ""}`
            }
          >
            <span className="app-header__nav-icon">{icon}</span>
            {!iconOnly && (
              <span className="app-header__nav-label">{label}</span>
            )}
          </NavLink>
        </Tooltip>
      ))}
    </nav>
  );
};

export default AppHeaderNav;
